import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';

import type { CreateOrderType, OrderType, UpdateOrderType } from '@klareospace/pharos-lib-types';

import type { OrderTypeFormInput } from '../schemas/order-type.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOrderTypeFormReturn {
  codeManuallyEdited: React.MutableRefObject<boolean>;
  form: UseFormReturn<OrderTypeFormInput>;
  handleFormSubmit: (data: OrderTypeFormInput) => Promise<void>;
  isPending: boolean;
}

interface UseOrderTypeFormOptions {
  isEditMode: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrderType | UpdateOrderType) => Promise<void>;
  orderType: OrderType | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOrderTypeForm({
  isEditMode,
  isPending,
  onClose,
  onSubmit,
  orderType,
}: UseOrderTypeFormOptions): UseOrderTypeFormReturn {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const codeManuallyEdited = useRef(false);

  // ─── Schema ───────────────────────────────────────────────────────────────

  const schema = useMemo(
    () =>
      z.object({
        code: isEditMode
          ? z.string().trim().min(1, tCommon('validation.codeRequired'))
          : z
              .string()
              .trim()
              .min(1, tCommon('validation.codeRequired'))
              .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t('orderTypes.drawer.validation.codeFormat')),
        label: z.string().trim().min(1, t('common.labelRequired')),
      }),
    [isEditMode, t],
  );

  // ─── Form ─────────────────────────────────────────────────────────────────

  const form = useForm<OrderTypeFormInput>({
    defaultValues: {
      code: isEditMode && orderType ? orderType.code : '',
      label: isEditMode && orderType ? orderType.label : '',
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  // ─── Submit ───────────────────────────────────────────────────────────────

  async function handleFormSubmit(data: OrderTypeFormInput): Promise<void> {
    if (isPending) return;
    const submitData: CreateOrderType | UpdateOrderType = isEditMode
      ? { label: data.label }
      : { code: data.code, label: data.label };

    try {
      await onSubmit(submitData);
      codeManuallyEdited.current = false;
      form.reset();
      onClose();
    } catch (error: unknown) {
      handleSubmitError(error, { form, isEditMode, t });
    }
  }

  return {
    codeManuallyEdited,
    form,
    handleFormSubmit,
    isPending,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SubmitErrorContext = {
  form: UseFormReturn<OrderTypeFormInput>;
  isEditMode: boolean;
  t: TFunction;
};

function handleSubmitError(error: unknown, ctx: SubmitErrorContext): void {
  const detail = getApiErrorDetail(error);

  if (detail.errorCode === 'Conflict.DuplicateOrderTypeCode') {
    ctx.form.setError('code', { message: ctx.t('orderTypes.drawer.validation.codeDuplicate') });
    return;
  }

  if (detail.errorCode === 'Conflict.DuplicateOrderTypeLabel') {
    ctx.form.setError('label', { message: ctx.t('orderTypes.drawer.validation.labelDuplicate') });
    return;
  }

  const fallbackKey = ctx.isEditMode ? 'orderTypes.actions.edit.error' : 'orderTypes.actions.create.error';
  toast.error(detail.message || ctx.t(fallbackKey));
}
